// Payment Modal Component
import { EcommerceTracking } from './ecommerce-tracking.js';

export function initPaymentModal() {
    const modal = document.getElementById('paymentModal');
    if (!modal) return;

    const buyNowBtn = document.getElementById('buyNowBtn');
    const closeBtn = modal.querySelector('.payment-modal-close');
    const overlay = modal.querySelector('.payment-modal-overlay');
    const form = document.getElementById('paymentForm');
    const tabs = modal.querySelectorAll('.payment-tab');
    const panels = modal.querySelectorAll('.payment-panel');

    // Store product data
    let currentProduct = null;
    // Stripe state
    let stripe = null;
    let elements = null;
    let cardElement = null;
    let expiryElement = null;
    let cvcElement = null;
    let addressElement = null;

    // Open modal
    if (buyNowBtn) {
        buyNowBtn.addEventListener('click', function() {
            currentProduct = {
                id: this.dataset.productId,
                title: this.dataset.productTitle,
                price: this.dataset.productPrice,
                image: this.dataset.productImage,
                category: this.dataset.productCategory || 'Uncategorized'
            };
            
            // Track begin_checkout event (user clicked "Buy Now")
            EcommerceTracking.trackBeginCheckout({
                id: currentProduct.id,
                name: currentProduct.title,
                price: EcommerceTracking.parsePrice(currentProduct.price),
                category: currentProduct.category
            });
            
            openModal(currentProduct);
        });
    }

    // Close modal
    function closeModal() {
        modal.classList.remove('active');
        document.body.style.overflow = '';
        clearForm();
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);

    // ESC key to close
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            closeModal();
        }
    });

    // Open modal and populate data
    function openModal(product) {
        // Populate product info
        document.getElementById('paymentProductTitle').textContent = product.title;
        document.getElementById('paymentProductPrice').textContent = product.price;
        document.getElementById('paymentProductId').value = product.id;

        // Pre-fill customer info if previously provided
        populateSavedCustomerInfo();

        // Reset to step 1
        goToStep(1);

        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
    }

    // Step navigation
    function goToStep(stepNumber) {
        // Update step indicators
        document.querySelectorAll('.payment-step').forEach(step => {
            const stepNum = parseInt(step.dataset.step);
            step.classList.toggle('active', stepNum === stepNumber);
            step.classList.toggle('completed', stepNum < stepNumber);
        });

        // Update step containers
        document.querySelectorAll('.payment-step-container').forEach(container => {
            container.classList.toggle('active', parseInt(container.dataset.stepContent) === stepNumber);
        });

        // Initialize Stripe Elements when going to step 2
        if (stepNumber === 2) {
            initializeStripeElements();
        }
    }

    // Continue to payment button
    const continueBtn = document.getElementById('continueToPayment');
    if (continueBtn) {
        continueBtn.addEventListener('click', function() {
            // Validate customer info
            clearErrors();
            const customerInfo = getCustomerInfoFromForm();
            if (!validateCustomerInfo(customerInfo)) {
                return;
            }
            saveCustomerInfo(customerInfo);
            goToStep(2);
        });
    }

    // Tab switching (deprecated): no-op since Stripe is default now
    tabs.forEach(tab => {
        tab.addEventListener('click', function() {
            panels.forEach(panel => panel.classList.remove('active'));
        });
    });

    // Form validation and submission (Card payment)
    if (form) {
        form.addEventListener('submit', async function(e) {
            e.preventDefault();

            // Clear previous errors
            clearErrors();
            clearMessages();

            // Get form data
            const formData = {
                productId: form.productId.value,
                firstName: form.firstName.value,
                lastName: form.lastName.value,
                email: form.email.value,
                termsAccept: document.getElementById('paymentTermsAccept')?.checked
            };

            // Validate
            if (!validateCustomerInfo(formData)) {
                return;
            }
            
            // Validate terms acceptance
            if (!formData.termsAccept) {
                const termsField = document.getElementById('paymentTermsAccept')?.closest('.payment-form-field');
                const errorSpan = termsField?.querySelector('.payment-field-error');
                if (errorSpan) {
                    errorSpan.textContent = 'You must accept the Terms of Service and Privacy Policy';
                    errorSpan.style.display = 'block';
                    termsField.classList.add('has-error');
                }
                return;
            }

            // Process card payment
            await processCardPayment(formData);
        });
    }

    function getCustomerInfoFromForm() {
        return {
            firstName: form?.firstName?.value || '',
            lastName: form?.lastName?.value || '',
            email: form?.email?.value || ''
        };
    }

    function saveCustomerInfo(data) {
        try {
            localStorage.setItem('paymentCustomerInfo', JSON.stringify(data));
        } catch (err) {
            console.warn('Unable to save customer info', err);
        }
    }

    function populateSavedCustomerInfo() {
        try {
            const raw = localStorage.getItem('paymentCustomerInfo');
            if (!raw) return;
            const data = JSON.parse(raw);
            if (data.firstName) form.firstName.value = data.firstName;
            if (data.lastName) form.lastName.value = data.lastName;
            if (data.email) form.email.value = data.email;
        } catch (err) {
            console.warn('Unable to load saved customer info', err);
        }
    }

    // Validation
    function validateCustomerInfo(data) {
        let isValid = true;

        if (!data.firstName || data.firstName.trim().length < 2) {
            showFieldError('paymentFirstName', 'First name must be at least 2 characters');
            isValid = false;
        }

        if (!data.lastName || data.lastName.trim().length < 2) {
            showFieldError('paymentLastName', 'Last name must be at least 2 characters');
            isValid = false;
        }

        if (!data.email || !isValidEmail(data.email)) {
            showFieldError('paymentEmail', 'Please enter a valid email address');
            isValid = false;
        }

        return isValid;
    }

    function isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    // Error handling
    function clearErrors() {
        const errorElements = modal.querySelectorAll('.payment-field-error');
        errorElements.forEach(el => {
            el.textContent = '';
            el.style.display = 'none';
        });

        const fields = modal.querySelectorAll('.payment-form-field');
        fields.forEach(field => field.classList.remove('has-error'));
    }

    function showFieldError(fieldId, message) {
        const input = document.getElementById(fieldId);
        if (input) {
            const field = input.closest('.payment-form-field');
            const errorSpan = field.querySelector('.payment-field-error');
            
            if (errorSpan) {
                errorSpan.textContent = message;
                errorSpan.style.display = 'block';
                field.classList.add('has-error');
            }
        }
    }

    function clearMessages() {
        const messagesDiv = modal.querySelector('.payment-messages');
        messagesDiv.textContent = '';
        messagesDiv.className = 'payment-messages';
    }

    function showMessage(message, type) {
        const messagesDiv = modal.querySelector('.payment-messages');
        messagesDiv.textContent = message;
        messagesDiv.className = `payment-messages ${type}`;
    }

    function clearForm() {
        if (form) {
            form.reset();
            clearErrors();
            clearMessages();
        }
    }

    // Stripe: load SDK
    async function loadStripeJs() {
        if (window.Stripe) return window.Stripe;
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3';
            script.onload = () => resolve(window.Stripe);
            script.onerror = () => reject(new Error('Stripe.js failed to load'));
            document.head.appendChild(script);
        });
    }

    async function fetchStripeConfig() {
        const res = await fetch('/api/payment/stripe/config');
        if (!res.ok) throw new Error('Failed to load Stripe config');
        const data = await res.json();
        if (!data.success || !data.publishableKey) throw new Error(data.error || 'Stripe config error');
        return data.publishableKey;
    }

    async function initializeStripeElements() {
        try {
            await loadStripeJs();
            const publishableKey = await fetchStripeConfig();
            // Disable Link by not including it in the Stripe instance
            if (!stripe) stripe = window.Stripe(publishableKey, { 
                betas: [],
                apiVersion: '2024-11-20.acacia'
            });
            if (!elements) {
                elements = stripe.elements({
                    appearance: {
                        disableAnimations: false
                    }
                });
                const style = { 
                    base: { 
                        fontSize: '16px', 
                        color: '#32325d',
                        '::placeholder': { color: '#aab7c4' }
                    }, 
                    invalid: { color: '#fa755a' } 
                };
                
                // Create separate card field elements with disableLink
                const options = { style };
                cardElement = elements.create('cardNumber', { ...options, showIcon: true, disableLink: true });
                expiryElement = elements.create('cardExpiry', options);
                cvcElement = elements.create('cardCvc', options);
                
                // Get customer name from step 1
                const firstName = document.getElementById('paymentFirstName')?.value || '';
                const lastName = document.getElementById('paymentLastName')?.value || '';
                const fullName = `${firstName} ${lastName}`.trim();
                
                // Create address element with Poland default and pre-filled name
                addressElement = elements.create('address', {
                    mode: 'billing',
                    defaultValues: {
                        name: fullName,
                        address: {
                            country: window.__countryCode || 'CH'
                        }
                    }
                });
                
                // Mount elements
                const cardContainer = document.getElementById('stripe-card-element');
                const expiryContainer = document.getElementById('stripe-expiry-element');
                const cvcContainer = document.getElementById('stripe-cvc-element');
                const addressContainer = document.getElementById('stripe-address-element');
                
                if (cardContainer && !cardContainer.dataset.mounted) {
                    cardElement.mount('#stripe-card-element');
                    cardContainer.dataset.mounted = 'true';
                }
                if (expiryContainer && !expiryContainer.dataset.mounted) {
                    expiryElement.mount('#stripe-expiry-element');
                    expiryContainer.dataset.mounted = 'true';
                }
                if (cvcContainer && !cvcContainer.dataset.mounted) {
                    cvcElement.mount('#stripe-cvc-element');
                    cvcContainer.dataset.mounted = 'true';
                }
                if (addressContainer && !addressContainer.dataset.mounted) {
                    addressElement.mount('#stripe-address-element');
                    addressContainer.dataset.mounted = 'true';
                }
                
                // Setup terms checkbox listener
                setupTermsCheckbox();
            }
        } catch (err) {
            console.error('Stripe init error:', err);
            showMessage(err.message || 'Unable to load Stripe right now.', 'error');
        }
    }
    
    // Setup terms checkbox to enable/disable pay button
    function setupTermsCheckbox() {
        const termsCheckbox = document.getElementById('paymentTermsAccept');
        const payButton = document.getElementById('cardPayButton');
        
        if (!termsCheckbox || !payButton) return;
        
        // Initially disable if not checked
        payButton.disabled = !termsCheckbox.checked;
        
        // Listen for changes
        termsCheckbox.addEventListener('change', function() {
            payButton.disabled = !this.checked;
            
            // Clear error if they check the box
            if (this.checked) {
                const termsField = this.closest('.payment-form-field');
                const errorSpan = termsField?.querySelector('.payment-field-error');
                if (errorSpan) {
                    errorSpan.textContent = '';
                    errorSpan.style.display = 'none';
                    termsField.classList.remove('has-error');
                }
            }
        });
    }

    // Payment processing via Stripe
    async function processCardPayment(formData) {
        showProcessing(true);

        try {
            if (!stripe || !elements || !cardElement) {
                await initializeStripeElements();
            }

            // Create PaymentIntent
            const createRes = await fetch('/api/payment/create-intent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: formData.productId,
                    customerInfo: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email
                    }
                })
            });
            const createData = await createRes.json();
            if (!createRes.ok || !createData.success) {
                throw new Error(createData.error || 'Failed to create payment');
            }

            // Get address data from Address Element
            const addressValue = await addressElement.getValue();
            
            // Confirm card payment with Elements
            const { paymentIntent, error } = await stripe.confirmCardPayment(createData.clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: addressValue.value.name || `${formData.firstName} ${formData.lastName}`,
                        email: formData.email,
                        address: addressValue.value.address
                    }
                }
            });
            if (error) throw error;
            if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                throw new Error('Payment not completed');
            }

            // Finalize order on server and send emails
            const finalizeRes = await fetch('/api/payment/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    paymentIntentId: paymentIntent.id,
                    productId: formData.productId,
                    customerInfo: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        email: formData.email,
                        country: (window.__countryCode || 'US')
                    }
                })
            });
            const finalizeData = await finalizeRes.json();
            if (!finalizeRes.ok || !finalizeData.success) {
                throw new Error(finalizeData.error || 'Failed to complete order');
            }

            // Track successful purchase
            const product = window.paymentModal?.getCurrentProduct?.();
            if (product) {
                EcommerceTracking.trackPurchase({
                    transactionId: paymentIntent.id,
                    product: {
                        id: product.id,
                        name: product.title,
                        price: EcommerceTracking.parsePrice(product.price),
                        category: product.category
                    }
                });
            }

            // Mark step 2 as completed
            const step2 = document.querySelector('.payment-step[data-step="2"]');
            if (step2) {
                step2.classList.add('completed');
                step2.classList.remove('active');
            }
            
            // Hide card elements on success
            const cardContainer = document.querySelector('[data-step-content="2"]');
            if (cardContainer) cardContainer.style.display = 'none';
            
            showMessage('Payment successful! Check your email for the download link.', 'success');
        } catch (error) {
            console.error('Payment error:', error);
            const errMsg = error?.message || 'Payment failed. Please try again.';
            showMessage(errMsg, 'error');
            const errSpan = document.getElementById('stripe-card-errors');
            if (errSpan) { errSpan.textContent = errMsg; errSpan.style.display = 'block'; }
        } finally {
            showProcessing(false);
        }
    }

    function showProcessing(show) {
        const processingOverlay = modal.querySelector('.payment-processing');
        const submitBtn = document.getElementById('cardPayButton');
        const btnText = submitBtn?.querySelector('.payment-btn-text');
        const btnLoader = submitBtn?.querySelector('.payment-btn-loader');

        if (show) {
            processingOverlay.style.display = 'flex';
            if (submitBtn) {
                submitBtn.disabled = true;
            }
            if (btnText) btnText.style.display = 'none';
            if (btnLoader) btnLoader.style.display = 'inline';
        } else {
            processingOverlay.style.display = 'none';
            if (submitBtn) {
                submitBtn.disabled = false;
            }
            if (btnText) btnText.style.display = 'inline';
            if (btnLoader) btnLoader.style.display = 'none';
        }
    }

    // Expose for Stripe integration
    window.paymentModal = {
        showMessage,
        showProcessing,
        closeModal,
        getCurrentProduct: () => currentProduct
    };
}
